/**
 * ATTEST-EVIDENCE-REQUIRED-1 (item 2) — deriving the Step-3 checklist.
 *
 * The letter type publishes `required_documents`: a declarative promise about
 * what will satisfy the engine. This module turns that list, plus whatever the
 * CPA has attached so far, into per-row status — and nothing else. It is pure
 * and synchronous so it can be tested without a DOM.
 *
 * **It does not predict a refusal.** Every `status` here is a description of the
 * form's current contents, not a verdict: `satisfied` means "a document of an
 * accepted kind is attached", not "the engine verified it". The five evidence
 * gates (attachment → kind → entity/period → figure → preparer) run on the
 * server against the hydrated document, and they are the only thing that decides
 * whether a letter builds. That is why the copy says *attached*, not
 * *verified*, and why `mismatch` is advisory: a row whose uploads are all of
 * some other kind is worth flagging to the CPA, but the client is reading a
 * classifier label off the upload response, and disagreeing with the server
 * about it in a way that reads as a block would be the UI inventing a refusal.
 *
 * Since item 3 a row may *also* carry `verification` — the server's answer,
 * once there is one. It is a separate field rather than a sixth
 * `ChecklistRowStatus` precisely to keep the paragraph above true: `status`
 * stays a statement about the form, `verification` is a statement about the
 * documents, and the two are sourced differently. Only the second may use the
 * word "verified".
 *
 * Two axes, deliberately kept apart:
 *
 *  - `RequiredDocument.requirement` — the *row* axis. How badly the checklist
 *    wants this document.
 *  - `AllowedAssertionSpec.requires_attached_source` — the *assertion* axis.
 *    Whether an attestation ("supported by records in my file") is even an
 *    available answer.
 *
 * A row can be `required` because of one assertion's figure and still carry a
 * second, waivable assertion. So attestability is decided per assertion and
 * then folded: a row is attestable only when *every* in-play assertion on it is
 * waivable. Reading it off `requirement` instead would offer a tick that the
 * server will not honour — worse than not offering one.
 */
import type {
  AllowedAssertionSpec,
  AttestPolicy,
  GateFinding,
  LetterTypeSummary,
  RequiredDocument,
} from '@/components/attest/api'
import type { StatementDraft } from '@/components/attest/formState'
import type {
  VerificationMap,
  VerificationState,
} from '@/components/attest/verificationResult'

/**
 * Where a row stands. Ordered loosely by how much it wants attention.
 *
 * `not_used` is not a failure: it is a row for an assertion no statement has
 * selected. Those still render — muted — because "what this letter type will
 * ask for" is useful before anything has been written, and because a row that
 * vanished when unused would make the list flicker as statements are edited.
 */
export type ChecklistRowStatus =
  | 'satisfied'
  | 'attested'
  | 'mismatch'
  | 'missing'
  | 'not_used'

/**
 * What the engine said about the documents on this row, folded across every
 * statement that draws on it. `null` until a build has been attempted — and
 * back to `null` the moment the form is edited, because a verdict about the
 * previous inputs is not a verdict about these ones.
 */
export interface RowVerification {
  state: VerificationState
  /** The server's findings, with the prose the UI renders verbatim. */
  findings: GateFinding[]
}

export interface ChecklistRow {
  key: string
  label: string
  description: string
  /** `true` when the row's document is not optional for the assertions in play. */
  isRequired: boolean
  perPeriod: boolean
  /** Which of the row's assertions a statement has actually selected. */
  assertionsInPlay: string[]
  /**
   * True when every in-play assertion is `requires_attached_source: false`, so
   * ticking "supported by records in my file" is a lawful answer for this row.
   * False whenever any in-play assertion is always-file — including the mixed
   * case, where the file is the only way to satisfy the strictest one.
   */
  attestable: boolean
  status: ChecklistRowStatus
  /** The server's verdict on this row's documents, or `null` if it has not spoken. */
  verification: RowVerification | null
  /** Indices of the statements that draw on this row, for the "draws on" hint. */
  statementIndexes: number[]
  /** Attached evidence whose classified kind this row accepts. */
  matchedFilenames: string[]
  /** Gates the row promises will run — `kind`, `entity`, `period`, `figure`. */
  matchChecks: string[]
}

export interface ChecklistSummary {
  rows: ChecklistRow[]
  /** Rows an assertion in the form actually put in play. */
  inPlay: ChecklistRow[]
  /** Of those, how many are `satisfied` or `attested`. */
  done: number
  total: number
  /**
   * Of those, how many the engine confirmed — `null` when it has not judged this
   * form. `null` and `0` are different sentences: "not checked yet" and "checked,
   * none of them held up". The progress line has to say which.
   */
  verified: number | null
}

const ANY_KIND = '*'

/** A row accepts this upload's classified kind. */
function acceptsKind(row: RequiredDocument, kind: string | undefined): boolean {
  if (row.document_kinds.includes(ANY_KIND)) return true
  if (!kind) return false
  return row.document_kinds.includes(kind)
}

/**
 * A statement counts as *started* once it has an assertion selected. Text alone
 * is not enough: the row a sentence draws on is keyed by assertion id, and a
 * half-typed sentence with no assertion yet points at nothing.
 */
function startedStatements(statements: StatementDraft[]): Array<[number, StatementDraft]> {
  return statements
    .map((s, i) => [i, s] as [number, StatementDraft])
    .filter(([, s]) => s.assertion_id.trim() !== '')
}

/**
 * Whether an assertion may be answered with an attestation instead of a file.
 *
 * Defaults to `false` when the policy has not loaded or does not name the
 * assertion. That is the safe direction: an unknown assertion is treated as
 * always-file, so the UI withholds a tick it is not sure about rather than
 * offering a waiver the server will refuse to honour.
 */
function isWaivable(
  assertionId: string,
  specs: Map<string, AllowedAssertionSpec>,
): boolean {
  const spec = specs.get(assertionId)
  return spec ? !spec.requires_attached_source : false
}

/**
 * Fold the per-statement verdicts of the statements on one row into one.
 *
 * Worst wins, and "worst" is the same order the engine uses: a contradicted
 * document is not softened by a second document that matched. Returns `null`
 * when the engine has said nothing about any of this row's statements, which is
 * how a row that was never sent stays honestly unjudged.
 */
function foldVerification(
  statementIndexes: number[],
  verification: VerificationMap | null,
): RowVerification | null {
  if (!verification) return null
  const verdicts = statementIndexes
    .map(i => verification.get(i))
    .filter((v): v is NonNullable<typeof v> => Boolean(v))
  if (!verdicts.length) return null

  const findings = verdicts.flatMap(v => v.findings)
  const state: VerificationState = verdicts.some(v => v.state === 'failed')
    ? 'failed'
    : verdicts.some(v => v.state === 'review')
      ? 'review'
      : 'verified'
  return { state, findings }
}

/**
 * `verification` is the server's answer for the last build, or `null` before
 * there has been one. Passing it is what turns "attached" into "verified"; not
 * passing it leaves this function exactly as item 2 left it.
 */
export function buildChecklist(
  letterType: LetterTypeSummary | null,
  statements: StatementDraft[],
  policy: AttestPolicy | null,
  verification: VerificationMap | null = null,
): ChecklistSummary {
  if (!letterType) {
    return { rows: [], inPlay: [], done: 0, total: 0, verified: null }
  }

  const specs = new Map((policy?.allowed_assertions ?? []).map(a => [a.id, a]))
  const started = startedStatements(statements)

  const rows: ChecklistRow[] = letterType.required_documents.map(doc => {
    const drawnFrom = started.filter(
      ([, s]) => doc.supports_assertions.includes(s.assertion_id.trim()),
    )
    const assertionsInPlay = Array.from(
      new Set(drawnFrom.map(([, s]) => s.assertion_id.trim())),
    )

    // Every attachment on every statement that draws on this row. A CPA who
    // uploaded the 1120S under one of two statements has uploaded it, and
    // asking for it twice would be the form failing to read its own contents.
    const attached = drawnFrom.flatMap(([, s]) => s.evidence.filter(e => e.reference.trim()))
    const matched = attached.filter(e => acceptsKind(doc, e.documentKind))
    const attested = drawnFrom.some(([, s]) => s.supportedByRecordsOnFile)

    const attestable =
      assertionsInPlay.length > 0 && assertionsInPlay.every(id => isWaivable(id, specs))

    let status: ChecklistRowStatus
    if (assertionsInPlay.length === 0) {
      status = 'not_used'
    } else if (matched.length > 0) {
      status = 'satisfied'
    } else if (attestable && attested) {
      status = 'attested'
    } else if (attached.length > 0) {
      // Something is attached, but nothing the row's kinds accept. Advisory —
      // the classifier may yet disagree with itself, and the server re-reads
      // the document anyway.
      status = 'mismatch'
    } else {
      status = 'missing'
    }

    const statementIndexes = drawnFrom.map(([i]) => i)

    return {
      key: doc.key,
      label: doc.label,
      description: doc.description,
      isRequired: doc.requirement === 'required',
      perPeriod: doc.per_period,
      assertionsInPlay,
      attestable,
      status,
      verification: foldVerification(statementIndexes, verification),
      statementIndexes,
      matchedFilenames: matched.map(e => e.filename ?? e.reference.trim()),
      matchChecks: doc.match_checks,
    }
  })

  // The denominator is rows actually in play. A letter that uses two of a
  // type's five assertions should not read "2 of 5" forever, which is a
  // progress bar telling the CPA they are failing at something they were never
  // asked to do.
  const inPlay = rows.filter(r => r.status !== 'not_used')
  const done = inPlay.filter(r => r.status === 'satisfied' || r.status === 'attested').length
  const verified = verification
    ? inPlay.filter(r => r.verification?.state === 'verified').length
    : null

  return { rows, inPlay, done, total: inPlay.length, verified }
}

/**
 * The checklist rows one statement draws on — the "you attach it here" hint
 * rendered above that statement's evidence block.
 */
export function rowsForStatement(
  summary: ChecklistSummary,
  statementIndex: number,
): ChecklistRow[] {
  return summary.rows.filter(r => r.statementIndexes.includes(statementIndex))
}

/**
 * Whether this statement should be offered the attestation tick.
 *
 * Only when its assertion is waivable *and* the type names a document for it —
 * an assertion with no checklist row has nothing to attest in place of, and a
 * tick there would be a box with no meaning attached to it.
 */
export function attestationOfferedFor(
  summary: ChecklistSummary,
  statementIndex: number,
): boolean {
  const rows = rowsForStatement(summary, statementIndex)
  return rows.length > 0 && rows.every(r => r.attestable)
}
